import type { FC, ReactNode } from "react";
import { useState } from "react";
import { Box, Button, Stack, Step, StepLabel, Stepper as MuiStepper, Typography } from "@mui/material";
import type { StepIconProps } from "@mui/material/StepIcon";
import { useTranslation, type TranslationKey } from "@privacy/translations";
import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import type { ComplianceWithAnswers } from "../../api/partners";
import { requestPartnerManualEntryMutationOptions, withdrawAssessmentRequestMutationOptions } from "../../api/partners";
import { logError } from "../../api/logError";
import { CheckIcon } from "../../shared/icons";
import { RequestAssessmentDialog } from "./RequestAssessmentDialog";

type ComplianceLifecycleInfoProps = {
  partnerName: string;
  compliance: ComplianceWithAnswers;
};

export const ComplianceLifecycleInfo: FC<ComplianceLifecycleInfoProps> = ({ partnerName, compliance }) => {
  const { t, formatDate } = useTranslation();

  return (
    <Stack sx={{ flex: 1, py: 4, gap: 2 }}>
      {(() => {
        switch (true) {
          case compliance.externalAssessmentRequestStatus === "awaiting-response":
            return (
              <Wrapper>
                <TitlesWrapper>
                  <Title>{t("Compliance.Subdrawer.Lifecycle.Pending.Title")}</Title>
                  <SubTitle>
                    {t("Compliance.Subdrawer.Lifecycle.Pending.SubTitle", {
                      date: formatDate(compliance.requestedAt, { showTime: false }),
                      email: compliance.requestedEmail,
                    })}
                  </SubTitle>
                  <Actions>
                    <SendReminderButton
                      partnerName={partnerName}
                      complianceId={compliance.id}
                      email={compliance.requestedEmail}
                    />
                    <WithdrawRequestButton complianceId={compliance.id} />

                    {compliance.external && <EnterAnswersManuallyButton complianceId={compliance.id} />}
                  </Actions>
                </TitlesWrapper>
                <Stepper activeStep={1} />
              </Wrapper>
            );
          case compliance.external &&
            isTimeToReview(compliance.external.answeredAt, compliance.evaluation.reviewed?.at):
            return (
              <Wrapper>
                <TitlesWrapper>
                  <Title>{t("Compliance.Subdrawer.Lifecycle.Review.Title")}</Title>
                  <SubTitle>
                    {t("Compliance.Subdrawer.Lifecycle.Review.SubTitle", {
                      date: formatDate(compliance.external.answeredAt, { showTime: false }),
                      name: `${compliance.external.answeredBy.firstName} ${compliance.external.answeredBy.lastName}`,
                    })}
                  </SubTitle>
                </TitlesWrapper>
                <Stepper activeStep={2} />
              </Wrapper>
            );
          case !!compliance.evaluation?.reviewed:
            return (
              <Wrapper>
                <TitlesWrapper>
                  <Title>{t("Compliance.Subdrawer.Lifecycle.Completed.Title")}</Title>
                  <SubTitle>
                    {t("Compliance.Subdrawer.Lifecycle.Completed.SubTitle", {
                      reviewer: `${compliance.evaluation.reviewed.by.firstName} ${compliance.evaluation.reviewed.by.lastName}`,
                    })}
                  </SubTitle>
                  <Actions>
                    <RequestAnswersButton partnerName={partnerName} complianceId={compliance.id} />
                    {compliance.external && <EnterAnswersManuallyButton complianceId={compliance.id} />}
                  </Actions>
                </TitlesWrapper>
                {compliance.external && (
                  <Stepper
                    activeStep={
                      isTimeToReview(compliance.external.answeredAt, compliance.evaluation.reviewed?.at) ? 2 : 3
                    }
                  />
                )}
              </Wrapper>
            );
          case !!compliance.manual:
            return (
              <Wrapper>
                <TitlesWrapper>
                  <Title>{t("Compliance.Subdrawer.Lifecycle.Empty.Title")}</Title>
                  <SubTitle>{t("Compliance.Subdrawer.Lifecycle.Empty.SubTitle")}</SubTitle>
                  <SubTitle>{t("Compliance.Subdrawer.Lifecycle.Empty.Description")}</SubTitle>
                  <Actions>
                    <RequestAnswersButton partnerName={partnerName} complianceId={compliance.id} />
                  </Actions>
                </TitlesWrapper>
              </Wrapper>
            );
          default:
            logError(`Unhandled compliance lifecycle info: ${JSON.stringify(compliance)}`);
            return null;
        }
      })()}
    </Stack>
  );
};

const Wrapper = ({ children }: { children: ReactNode }) => (
  <Stack direction="row" sx={{ justifyContent: "space-between", gap: 3 }}>
    {children}
  </Stack>
);

const TitlesWrapper = ({ children }: { children: ReactNode }) => <Stack sx={{ gap: 0.5 }}>{children}</Stack>;

export const Title = ({ children }: { children: ReactNode }) => (
  <Typography variant="h3" sx={{ fontSize: theme => theme.typography.pxToRem(14), fontWeight: "600" }}>
    {children}
  </Typography>
);

export const SubTitle = ({ children }: { children: ReactNode }) => (
  <Typography
    sx={{
      fontSize: theme => theme.typography.pxToRem(12),
      color: theme => theme.palette.text.tertiary,
      lineHeight: 1.2,
    }}
  >
    {children}
  </Typography>
);

const Actions = ({ children }: { children: ReactNode }) => (
  <Stack direction="row" sx={{ mt: 2, gap: 1 }}>
    {children}
  </Stack>
);

const RequestAnswersButton = ({ partnerName, complianceId }: { partnerName: string; complianceId: string }) => {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <>
      <Button variant="contained" size="small" onClick={() => setIsDialogOpen(true)}>
        {t("Compliance.Subdrawer.Lifecycle.Actions.SendQuestionnaire")}
      </Button>

      {isDialogOpen && (
        <RequestAssessmentDialog
          partnerName={partnerName}
          onClose={() => setIsDialogOpen(false)}
          complianceId={complianceId}
        />
      )}
    </>
  );
};

const SendReminderButton = ({
  partnerName,
  complianceId,
  email,
}: {
  partnerName: string;
  complianceId: string;
  email?: string;
}) => {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <>
      <Button variant="outlined" size="small" onClick={() => setIsDialogOpen(true)}>
        {t("Compliance.Subdrawer.Lifecycle.Actions.SendReminder")}
      </Button>

      {isDialogOpen && (
        <RequestAssessmentDialog
          partnerName={partnerName}
          onClose={() => setIsDialogOpen(false)}
          complianceId={complianceId}
          defaultEmail={email}
        />
      )}
    </>
  );
};

const WithdrawRequestButton = ({ complianceId }: { complianceId: string }) => {
  const { t } = useTranslation();
  const withdrawAssessmentRequestMutation = useMutation(withdrawAssessmentRequestMutationOptions({ complianceId }));

  return (
    <Button
      loading={withdrawAssessmentRequestMutation.isPending}
      variant="outlined"
      size="small"
      onClick={() => withdrawAssessmentRequestMutation.mutate()}
    >
      {t("Compliance.Subdrawer.Lifecycle.Actions.WithdrawRequest")}
    </Button>
  );
};

const EnterAnswersManuallyButton = ({ complianceId }: { complianceId: string }) => {
  const { t } = useTranslation();
  const requestPartnerManualEntryMutation = useMutation(requestPartnerManualEntryMutationOptions({ complianceId }));

  return (
    <Button
      loading={requestPartnerManualEntryMutation.isPending}
      variant="text"
      size="small"
      onClick={() => requestPartnerManualEntryMutation.mutate()}
    >
      {t("Compliance.Subdrawer.Lifecycle.Actions.EnterAnswersManually")}
    </Button>
  );
};

export const isTimeToReview = (answeredAt?: string, reviewedAt?: string) => {
  if (!answeredAt) return false;
  if (!reviewedAt) return true;

  return dayjs(answeredAt).isAfter(dayjs(reviewedAt));
};

type Step = 0 | 1 | 2 | 3;

const StepIcon = ({ active, completed }: StepIconProps) => {
  if (completed) {
    return (
      <Box
        sx={theme => ({
          width: 16,
          height: 16,
          borderRadius: "50%",
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        })}
      >
        <CheckIcon size={10} />
      </Box>
    );
  }

  return (
    <Box
      sx={theme => ({
        width: 16,
        height: 16,
        borderRadius: "50%",
        boxSizing: "border-box",
        border: `2px solid ${active ? theme.palette.primary[500] : theme.palette.primary[200]}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      })}
    >
      {active && (
        <Box
          sx={theme => ({
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: theme.palette.primary[500],
          })}
        />
      )}
    </Box>
  );
};

const Stepper = ({ activeStep }: { activeStep: Step }) => {
  const { t } = useTranslation();

  return (
    <Stack direction="row" sx={{ width: "160px" }}>
      <MuiStepper
        sx={theme => ({
          "& .MuiStepLabel-root": { padding: "3px 0" },
          "& .MuiStepConnector-vertical": { marginLeft: "7px" },
          "& .MuiStepConnector-line": {
            minHeight: "8px",
            borderColor: theme.palette.primary[200],
          },
          "& .MuiStepLabel-label": {
            "fontSize": theme.typography.pxToRem(12),
            "color": theme.palette.grey["500"],

            "&.Mui-completed": { color: theme.palette.text.primary },
            "&.Mui-active": { color: theme.palette.text.primary, fontWeight: 600 },
          },
        })}
        orientation="vertical"
        activeStep={activeStep}
      >
        {STEPS.map(step => (
          <Step key={step.key}>
            <StepLabel slots={{ stepIcon: StepIcon }}>{t(step.label(activeStep))}</StepLabel>
          </Step>
        ))}
      </MuiStepper>
    </Stack>
  );
};

const STEPS = [
  { key: "started", label: () => "Compliance.Subdrawer.Lifecycle.Stepper.Started.Label" },
  {
    key: "response",
    label: activeStep =>
      activeStep > 1
        ? "Compliance.Subdrawer.Lifecycle.Stepper.Responded.Label"
        : "Compliance.Subdrawer.Lifecycle.Stepper.AwaitingResponse.Label",
  },
  { key: "review", label: () => "Compliance.Subdrawer.Lifecycle.Stepper.TimeToReview.Label" },
] as const satisfies Array<{ key: string; label: (activeStep: Step) => TranslationKey }>;
